import { useMemo, useRef, useState } from "react";
import type { CellValueChangedEvent, ColDef } from "ag-grid-community";
import {
  NJButton,
  NJFormItem,
  NJIcon,
  NJIconButton,
  NJInlineMessage,
  NJModal,
  NJModalContentInformation,
  NJModalContent,
  NJSelect,
} from "@engie-group/fluid-design-system-react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Size2MixSimpleTable } from "@shared/size2mix-shell";
import type { Size2MixSimpleTableColumn } from "@shared/size2mix-shell";
import type { TopologyParameters } from "./types";

export type PipeCatalog = TopologyParameters["pipeCatalog"];
export type PipeCatalogRow = PipeCatalog["rows"][number];

export const PIPE_CATALOG_COLUMNS: {
  key: string;
  label: string;
  editable?: boolean;
}[] = [
  { key: "dn", label: "Diamètre nominal (mm)*", editable: true },
  { key: "innerDiameter", label: "Diamètre intérieur (mm)*", editable: true },
  { key: "pressureGradient", label: "Gradient de pression maximal (mmCE/m)" },
  { key: "maxVelocity", label: "Vitesse maximale (m/s)" },
  { key: "roughness", label: "Rugosité (mm)", editable: true },
  {
    key: "thermalCoef",
    label: "Coefficient thermique (W/m/K)",
    editable: true,
  },
  { key: "cost", label: "Coût (€)" },
];

/** Read-only columns of the catalogue grid: flex, min 130px, centred, no filter (as in production). */
export const PIPE_CATALOG_TABLE_COLUMNS: Size2MixSimpleTableColumn<PipeCatalogRow>[] =
  PIPE_CATALOG_COLUMNS.map((c) => ({
    key: c.key,
    label: c.label,
    minWidth: 130,
    align: "center",
    filter: false,
  }));

/** Reference catalogues proposed by « Remplacer le catalogue » (mock, derived from the PUC EDM one). */
export function buildReferenceCatalogs(base: PipeCatalog): PipeCatalog[] {
  const variant = (name: string, thermalFactor: number, roughness = 0.1) => ({
    name,
    rows: base.rows.map((r) => ({
      ...r,
      roughness,
      thermalCoef: Math.round(r.thermalCoef * thermalFactor * 100) / 100,
    })),
  });
  return [
    variant("Axiom-Acier PN25_Isolation 2", 1.15),
    variant("Axiom-Acier PN25_Isolation 3", 1),
    variant("Axiom-Acier PN25_Isolation 1", 1.35),
    variant("PEHD PN16_Isolation 1", 1.4, 0.01),
  ];
}

export function downloadPipeCatalog(catalog: PipeCatalog) {
  const header = PIPE_CATALOG_COLUMNS.map((c) => c.label).join(";");
  const lines = catalog.rows.map((row) =>
    PIPE_CATALOG_COLUMNS.map(
      (c) => (row as Record<string, unknown>)[c.key] ?? "",
    ).join(";"),
  );
  const blob = new Blob([[header, ...lines].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${catalog.name}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const body = (children: React.ReactNode) => (
  <div
    className="nj-modal__body"
    style={{ maxHeight: "calc(100vh - 240px)", overflowY: "auto" }}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* « Modifier » — Édition du catalogue de diamètres du réseau           */
/* ------------------------------------------------------------------ */

export function EditPipeCatalogModal({
  catalog,
  onClose,
  onValidate,
}: {
  catalog: PipeCatalog;
  onClose: () => void;
  onValidate: (catalog: PipeCatalog) => void;
}) {
  const [history, setHistory] = useState<PipeCatalogRow[][]>([catalog.rows]);
  const [cursor, setCursor] = useState(0);
  const rows = history[cursor];

  const commit = (next: PipeCatalogRow[]) => {
    setHistory((h) => [...h.slice(0, cursor + 1), next]);
    setCursor((c) => c + 1);
  };

  const columnDefs = useMemo<ColDef<PipeCatalogRow & { __index: number }>[]>(
    () => [
      {
        colId: "rowNumber",
        headerName: "#",
        flex: 1,
        minWidth: 80,
        valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
        cellClass: "text-center",
      },
      ...PIPE_CATALOG_COLUMNS.map<ColDef<PipeCatalogRow & { __index: number }>>(
        (c) => ({
          colId: c.key,
          field: c.key as keyof PipeCatalogRow,
          headerName: c.label,
          flex: 1,
          minWidth: 120,
          editable: !!c.editable,
          cellDataType: c.editable ? "number" : undefined,
          valueFormatter: (p) =>
            p.value === undefined || p.value === null || p.value === ""
              ? "-"
              : String(p.value),
          cellStyle: { textAlign: "center" },
        }),
      ),
      {
        colId: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 100,
        sortable: false,
        cellRenderer: (p: { data?: { __index: number } }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <NJIconButton
              icon="delete_outline"
              label=""
              aria-label="Supprimer"
              variant="tertiary"
              onClick={() =>
                p.data && commit(rows.filter((_, i) => i !== p.data!.__index))
              }
            />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows],
  );

  const data = useMemo(() => rows.map((r, i) => ({ ...r, __index: i })), [rows]);

  const onCellValueChanged = (
    e: CellValueChangedEvent<PipeCatalogRow & { __index: number }>,
  ) => {
    if (!e.data || !e.colDef.field) return;
    const { __index, ...edited } = e.data;
    commit(rows.map((r, i) => (i === __index ? { ...r, ...edited } : r)));
  };

  const copy = () => {
    const text = [
      PIPE_CATALOG_COLUMNS.map((c) => c.label).join("\t"),
      ...rows.map((r) =>
        PIPE_CATALOG_COLUMNS.map(
          (c) => (r as Record<string, unknown>)[c.key] ?? "-",
        ).join("\t"),
      ),
    ].join("\n");
    void navigator.clipboard?.writeText(text);
  };

  return (
    <NJModal isOpen setIsOpen={(open) => !open && onClose()} closeOnEscape>
      <NJModalContent
        id="edit-pipe-catalog-modal"
        title="Édition du catalogue de diamètres du réseau"
        onClose={onClose}
        style={{ width: "90%", maxWidth: "90%" }}
        footer={
          <>
            <NJButton label="Annuler" variant="secondary" onClick={onClose} />
            <NJButton
              label="Valider"
              onClick={() => onValidate({ ...catalog, rows })}
            />
          </>
        }
      >
        {body(
          <>
            <NJFormItem
              id="pipe-catalog-name"
              label="Nom du catalogue"
              labelKind="static"
              value={catalog.name}
              readOnly
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 4,
                marginTop: 8,
              }}
            >
              <span
                className="nj-text nj-text--sm"
                style={{
                  color: "var(--nj-semantic-color-text-neutral-tertiary-default)",
                  marginRight: 4,
                }}
              >
                {rows.length} lignes
              </span>
              <NJIconButton
                icon="undo"
                label=""
                aria-label="Annuler la dernière modification"
                variant="tertiary"
                isDisabled={cursor === 0}
                onClick={() => setCursor((c) => Math.max(0, c - 1))}
              />
              <NJIconButton
                icon="redo"
                label=""
                aria-label="Rétablir la modification"
                variant="tertiary"
                isDisabled={cursor >= history.length - 1}
                onClick={() =>
                  setCursor((c) => Math.min(history.length - 1, c + 1))
                }
              />
              <NJIconButton
                icon="content_copy"
                label=""
                aria-label="Copier"
                variant="tertiary"
                onClick={copy}
              />
            </div>
            <div
              className="ag-theme-quartz"
              style={{ marginTop: 16, ["--ag-line-height" as string]: "30px" }}
            >
              <AgGridReact
                rowData={data}
                columnDefs={columnDefs}
                defaultColDef={{ sortable: true, resizable: true }}
                rowHeight={30}
                headerHeight={48}
                domLayout="autoHeight"
                stopEditingWhenCellsLoseFocus
                onCellValueChanged={onCellValueChanged}
              />
            </div>
          </>,
        )}
      </NJModalContent>
    </NJModal>
  );
}

/* ------------------------------------------------------------------ */
/* « Remplacer le catalogue » — Configuration du catalogue              */
/* ------------------------------------------------------------------ */

export function ConfigurePipeCatalogModal({
  references,
  onClose,
  onValidate,
}: {
  references: PipeCatalog[];
  onClose: () => void;
  onValidate: (catalog: PipeCatalog) => void;
}) {
  const [selected, setSelected] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [name, setName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const preview: PipeCatalog | undefined = useMemo(() => {
    if (fileName) return { name: fileName, rows: references[1]?.rows ?? [] };
    return references.find((r) => r.name === selected);
  }, [fileName, selected, references]);

  const pickFile = (file?: File) => {
    if (!file) return;
    setFileName(file.name.replace(/\.xlsx$/i, ""));
    setSelected(undefined);
    if (!name) setName(file.name.replace(/\.xlsx$/i, ""));
  };

  const box: React.CSSProperties = {
    border: "1px solid var(--nj-semantic-color-border-neutral-minimal-default)",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  };
  const h3: React.CSSProperties = {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "24px",
  };

  return (
    <NJModal isOpen setIsOpen={(open) => !open && onClose()} closeOnEscape>
      <NJModalContent
        id="configure-pipe-catalog-modal"
        title="Configuration du catalogue de diamètres du réseau"
        onClose={onClose}
        style={{ width: "90%", maxWidth: 840 }}
        footer={
          <>
            <NJButton label="Fermer" variant="secondary" onClick={onClose} />
            <NJButton
              label="Valider"
              disabled={!preview || !name.trim()}
              onClick={() =>
                preview && onValidate({ name: name.trim(), rows: preview.rows })
              }
            />
          </>
        }
      >
        {body(
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div style={box}>
              <h3 style={h3}>Catalogue de référence</h3>
              <NJSelect.Root
                id="pipe-catalog-reference"
                label="Sélectionnez un catalogue"
                labelKind="floating"
                buttonDefaultValueLabel="Sélectionnez un catalogue"
                listNavigationLabel="Utilisez les flèches haut et bas puis Entrée pour sélectionner"
                value={selected}
                onChange={(v) => {
                  setSelected(v);
                  setFileName(undefined);
                  if (v) setName(v);
                }}
              >
                {references.map((r) => (
                  <NJSelect.Item key={r.name} value={r.name}>
                    {r.name}
                  </NJSelect.Item>
                ))}
              </NJSelect.Root>
              <hr
                style={{
                  margin: 0,
                  border: 0,
                  borderTop:
                    "1px solid var(--nj-semantic-color-border-neutral-minimal-default)",
                }}
              />
              <h3 style={h3}>Ou importez votre catalogue (.xlsx)</h3>
              <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  inputRef.current?.click()
                }
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  pickFile(e.dataTransfer.files[0]);
                }}
                style={{
                  minHeight: 160,
                  border: `1px dashed var(--nj-semantic-color-border-neutral-${dragOver ? "strong" : "minimal"}-default)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                <span
                  className="material-icons-outlined"
                  aria-hidden="true"
                  style={{
                    fontSize: 40,
                    color: "var(--nj-semantic-color-icon-neutral-tertiary-default, var(--nj-semantic-color-text-neutral-tertiary-default))",
                  }}
                >
                  cloud_upload
                </span>
                {fileName ? (
                  <span className="nj-text nj-text--sm">
                    <strong>{fileName}.xlsx</strong>
                  </span>
                ) : (
                  <>
                    <span className="nj-text nj-text--sm">
                      Glissez-déposez le fichier ici ou
                    </span>
                    <span
                      className="nj-text nj-text--sm"
                      style={{ fontWeight: 700, textDecoration: "underline" }}
                    >
                      Cliquez pour télécharger
                    </span>
                  </>
                )}
                <span
                  className="nj-text nj-text--xs"
                  style={{
                    color:
                      "var(--nj-semantic-color-text-neutral-tertiary-default)",
                  }}
                >
                  Formats acceptés : .xlsx • Taille max : 10 Mo
                </span>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
              </div>
            </div>
            <NJFormItem
              id="pipe-catalog-new-name"
              label="Nom du catalogue"
              labelKind="static"
              value={name}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <h3
                style={{
                  ...h3,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <NJIcon name="preview" />
                Aperçu du catalogue de référence
              </h3>
              {preview ? (
                <Size2MixSimpleTable
                  rowHeight={30}
                  headerHeight={54}
                  wrapHeaderText={false}
                  columns={PIPE_CATALOG_TABLE_COLUMNS}
                  rows={preview.rows}
                  rowKey={(row) => String(row.dn)}
                />
              ) : (
                <NJInlineMessage variant="information">
                  Sélectionnez un catalogue de référence ou importez un fichier
                  catalogue au format (.xlsx) afin d'initialiser le tableau
                </NJInlineMessage>
              )}
            </div>
          </div>,
        )}
      </NJModalContent>
    </NJModal>
  );
}

/* ------------------------------------------------------------------ */
/* « Supprimer » — Confirmation de suppression                          */
/* ------------------------------------------------------------------ */

export function DeletePipeCatalogModal({
  catalogName,
  onClose,
  onConfirm,
}: {
  catalogName: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <NJModal isOpen setIsOpen={(open) => !open && onClose()} closeOnEscape>
      <NJModalContentInformation
        id="delete-pipe-catalog-confirmation"
        icon="info_outline"
        iconVariant="red"
        title="Confirmation de suppression"
        onClose={onClose}
        footer={
          <>
            <NJButton label="Annuler" variant="secondary" onClick={onClose} />
            <NJButton label="Supprimer" onClick={onConfirm} />
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Êtes-vous sûr de vouloir supprimer ce catalogue des canalisations ?
        </p>
        <p style={{ margin: 0, fontWeight: 700 }}>{catalogName}</p>
      </NJModalContentInformation>
    </NJModal>
  );
}
